import React, { useEffect, useState } from "react";
import { Settings, Plus, Trash2, Lock } from "lucide-react";
import ToggleSwitch from "@/components/ui/Switch";
import Button from "@/components/ui/Button";
import Dropdown from "@/components/ui/DropDown";
import TextField from "@/components/ui/Textfield";
import DatePickerField from "@/components/ui/DatePickerField";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import { isAlreadyRestored } from "@/api/restore";
import z from "zod";
import CampaignDetailWrapper from "./campaign-detail-wrapper";
import useCampaignStore from "../store/campaignStore";
import DeleteConfirmationControl from "@/pages/campaign-detail/components/delete-confirmation-control";

// Updated Types
interface Asset {
  /**
   * The value sent as `ad_row_id`, which the backend matches a saved Box by. For a
   * hydrated Box this is its persisted `assets_row_id`; for a new row it is a
   * client-generated id that becomes that column on save.
   */
  id: string;
  /**
   * The `assets_rows` primary key, present only on a Box that exists server-side. This is
   * what `DELETE /assets/{id}` needs, and its presence is what makes a row "saved" —
   * `id` cannot answer that, because a saved Box's `id` is the client-generated value it
   * was created with.
   */
  assetId?: string;
  publisher: string;
  adType: string;
  fields: { [key: string]: any };
  /**
   * The Box's creative has been briefed, so it is locked: shown read-only and
   * saved with exactly the parameters it already has. Re-deriving them from a
   * newer ad-category version would read as an edit and the server would reject
   * the whole save (423 ASSET_LOCKED).
   */
  locked?: boolean;
  savedParameters?: Array<{
    parameter_definition_id: string;
    parameter_key: string;
    parameter_value: string;
  }>;
}

interface AssetGroup {
  flight_id: string;
  flight_index: number;
  language_id: string;
  language_name: string;
  objective_id: string;
  region_name: string;
  category_id: string;
  flight_row_id: string;
  flight_ad_categories_id: string;
  assets: Asset[];
}

type SimplifiedAssetState = AssetGroup[];

const AssetSetup: React.FC = () => {
  const [selectedFlight, setSelectedFlight] = useState<number>(0);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("all");
  const [selectedCategories, setSelectedCategories] = useState<{
    [regionKey: string]: string;
  }>({});

  const [assetGroups, setAssetGroups] = useState<SimplifiedAssetState>([]);
  // Tracks "apply to all" toggle per category: key = "flightIndex-langId-objId-region-catId", value = publisher ID
  const [applyToAllPublisher, setApplyToAllPublisher] = useState<{
    [groupKey: string]: string;
  }>({});

  const campaign = useCampaignStore((state) => state.campaign);
  const {
    getAdCategoryData,
    assetsPageData,
    adCategoryData,
    uiParametersByAdType,
    getAssetsPageData,
    createAssets,
    parameterDefinitions,
    assetsValues,
    updateAssets,
    deleteAssetRequest,
    restoreAssetRequest,
  } = useCampaignStore(
    useShallow((state) => ({
      getAdCategoryData: state.getAdCategoryData,
      assetsPageData: state.assetsPageData,
      adCategoryData: state.adCategoryData,
      uiParametersByAdType: state.uiParametersByAdType,
      getAssetsPageData: state.getAssetsPageData,
      createAssets: state.createAssets,
      parameterDefinitions: state.parameterDefinitions,
      assetsValues: state.assetsValues,
      updateAssets: state.updateAssets,
      deleteAssetRequest: state.deleteAsset,
      restoreAssetRequest: state.restoreAsset,
    }))
  );

  const { setError, showSuccess, showInfo } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
      showInfo: state.showInfo,
    }))
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const createDefaultAsset = (): Asset => ({
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    publisher: "",
    adType: "",
    fields: {},
  });

  const findAssetGroup = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): AssetGroup | undefined => {
    return assetGroups.find(
      (group) =>
        group.flight_index === flightIndex &&
        group.language_id === languageId &&
        group.objective_id === objectiveId &&
        group.region_name === regionName &&
        group.category_id === categoryId
    );
  };

  const getOrCreateAssetGroup = (
    flightId: string,
    flightIndex: number,
    languageId: string,
    languageName: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    flightRowId: string,
    flightAdCategoriesId: string
  ): AssetGroup => {
    const existing = findAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );

    if (existing) {
      return existing;
    }

    const newGroup: AssetGroup = {
      flight_id: flightId,
      flight_index: flightIndex,
      language_id: languageId,
      language_name: languageName,
      objective_id: objectiveId,
      region_name: regionName,
      category_id: categoryId,
      flight_row_id: flightRowId,
      flight_ad_categories_id: flightAdCategoriesId,
      assets: [createDefaultAsset()],
    };

    setAssetGroups((prev) => [...prev, newGroup]);
    return newGroup;
  };

  const getCurrentAssets = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): Asset[] => {
    const group = findAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );
    return group?.assets || [];
  };

  const updateAssetGroup = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    updater: (prev: Asset[]) => Asset[]
  ) => {
    setAssetGroups((prev) =>
      prev.map((group) =>
        group.flight_index === flightIndex &&
        group.language_id === languageId &&
        group.objective_id === objectiveId &&
        group.region_name === regionName &&
        group.category_id === categoryId
          ? { ...group, assets: updater(group.assets) }
          : group
      )
    );
  };

  const addAsset = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      // A new row starts empty — the publisher is never preselected. Due dates
      // fill in only once the user picks the matching publisher themselves.
      (prev) => [...prev, createDefaultAsset()]
    );
  };

  // A Box is saved exactly when the server told us its primary key.
  const isPersistedAsset = (asset: Asset): boolean => !!asset.assetId;

  const deleteAsset = async (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    assetId: string
  ) => {
    const currentAssets = getCurrentAssets(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );
    const target = currentAssets.find((asset) => asset.id === assetId);

    // Substituting a blank default row here used to make the following
    // PUT /assets/{campaign_id} fail validation (flight_ad_categories is
    // required), so an empty group is refused outright instead.
    if (currentAssets.length <= 1) {
      setError(
        "A flight row must keep at least one asset. Delete the flight instead if it is no longer needed."
      );
      return;
    }

    if (target && isPersistedAsset(target)) {
      // Addressed by primary key, and only dropped locally once the server confirms.
      await deleteAssetRequest(target.assetId as string);
    }

    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      (prev) => prev.filter((asset) => asset.id !== assetId)
    );

    // Only a saved asset can be undone — an unsaved row never reached the
    // server, so there is nothing to restore.
    if (target && isPersistedAsset(target)) {
      showSuccess("Asset deleted.", undefined, {
        label: "Undo",
        onClick: () => handleUndoDeleteAsset(target.assetId as string),
      });
    }
  };

  // Undo of a just-completed delete, run straight from the toast. The wizard
  // rebuilds its local rows from the store, so the restored asset reappears via
  // the same fetch the page loads with. A 404 NO_RESTORABLE_DELETION means it is
  // already back (not a failure); a 409 names the ancestor to restore first.
  const handleUndoDeleteAsset = async (assetId: string): Promise<void> => {
    try {
      await restoreAssetRequest(assetId);
      await fetchData();
      showSuccess("Asset restored.");
    } catch (error: any) {
      if (isAlreadyRestored(error)) {
        await fetchData();
        showInfo("That asset has already been restored.");
        return;
      }
      setError(error?.message ?? "Failed to restore asset");
    }
  };

  const updateAssetField = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    assetId: string,
    field: string,
    value: any
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      // Top-level asset props (publisher, adType, …) are never shared between
      // rows — "Apply to same publisher" only covers the due date columns.
      // Switching a row onto the active publisher does pull that publisher's
      // due dates in, so the row lines up with the ones it just joined.
      (prev) => {
        const key = getApplyToAllKey(flightIndex, languageId, objectiveId, regionName, categoryId);
        const activePublisher = applyToAllPublisher[key];
        const joinsActivePublisher =
          field === "publisher" && !!activePublisher && value === activePublisher;
        const inheritedDueDates = joinsActivePublisher
          ? pickDueDateFields(
              prev.find((a) => a.id !== assetId && a.publisher === activePublisher)?.fields
            )
          : {};

        return prev.map((asset) =>
          asset.id === assetId
            ? {
                ...asset,
                [field]: value,
                fields: { ...asset.fields, ...inheritedDueDates },
              }
            : asset
        );
      }
    );
  };

  const updateAssetFields = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    assetId: string,
    fields: { [key: string]: any }
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      (prev) => {
        const sourceAsset = prev.find((a) => a.id === assetId);
        const key = getApplyToAllKey(flightIndex, languageId, objectiveId, regionName, categoryId);
        const activePublisher = applyToAllPublisher[key];
        // Only the due date fields follow the toggle across matching publishers.
        const dueDates = pickDueDateFields(fields);
        const shouldPropagate =
          sourceAsset &&
          activePublisher &&
          sourceAsset.publisher === activePublisher &&
          Object.keys(dueDates).length > 0;

        return prev.map((asset) => {
          if (asset.id === assetId) {
            return { ...asset, fields: { ...asset.fields, ...fields } };
          }
          if (shouldPropagate && asset.publisher === activePublisher) {
            return { ...asset, fields: { ...asset.fields, ...dueDates } };
          }
          return asset;
        });
      }
    );
  };

  const getSelectedCategory = (regionKey: string): string => {
    return selectedCategories[regionKey] || "";
  };

  const setSelectedCategory = (regionKey: string, categoryId: string) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [regionKey]: categoryId,
    }));
  };

  const getAssetCount = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): number => {
    return getCurrentAssets(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    ).length;
  };

  const getApplyToAllKey = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): string => `${flightIndex}-${languageId}-${objectiveId}-${regionName}-${categoryId}`;

  // "Apply to same publisher" only shares the due date columns
  // (Due to media / Due to publisher), never the ad type or other fields.
  const isDueDateFieldKey = (fieldKey: string): boolean =>
    fieldKey.startsWith("due_dates");

  const pickDueDateFields = (fields: { [key: string]: any } = {}) =>
    Object.fromEntries(
      Object.entries(fields).filter(([fieldKey]) => isDueDateFieldKey(fieldKey))
    );

  const isApplyToAllActive = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): boolean => {
    const key = getApplyToAllKey(flightIndex, languageId, objectiveId, regionName, categoryId);
    return !!applyToAllPublisher[key];
  };

  const toggleApplyToAll = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ) => {
    const key = getApplyToAllKey(flightIndex, languageId, objectiveId, regionName, categoryId);
    const isCurrentlyActive = !!applyToAllPublisher[key];

    if (isCurrentlyActive) {
      setApplyToAllPublisher((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      // Find the first asset with a publisher to use as source
      const currentAssets = getCurrentAssets(
        flightIndex, languageId, objectiveId, regionName, categoryId
      );
      const sourceAsset = currentAssets.find((a) => a.publisher);
      if (!sourceAsset) return;

      setApplyToAllPublisher((prev) => ({
        ...prev,
        [key]: sourceAsset.publisher,
      }));

      // Copy only the source asset's due dates to other rows with the same publisher
      updateAssetGroup(
        flightIndex,
        languageId,
        objectiveId,
        regionName,
        categoryId,
        (prev) => {
          const source = prev.find((a) => a.id === sourceAsset.id);
          if (!source) return prev;
          const dueDates = pickDueDateFields(source.fields);
          return prev.map((asset) =>
            asset.id !== source.id && asset.publisher === source.publisher
              ? { ...asset, fields: { ...asset.fields, ...dueDates } }
              : asset
          );
        }
      );
    }
  };

  const getTableColumns = (categoryId: string, currentAssets: Asset[]) => {
    if (!uiParametersByAdType || !categoryId) return [];

    const adCategoryParams = uiParametersByAdType[categoryId];
    if (!adCategoryParams) return [];

    const columnsMap = new Map();

    currentAssets.forEach((asset) => {
      if (!asset.adType) return;

      const adTypeData = adCategoryParams.find(
        (adType) => adType.id === asset.adType
      );
      if (!adTypeData) return;

      adTypeData.parameters.forEach((param) => {
        if (param.key === "due_dates") return;

        const variant = param.variants.find(
          (v) => v.ad_type_id === asset.adType
        );

        if (
          variant &&
          variant.value_type === "user_defined" &&
          variant.fields
        ) {
          variant.fields.forEach((field) => {
            const columnKey = `${param.key}_${field.key}`;
            if (!columnsMap.has(columnKey)) {
              columnsMap.set(columnKey, {
                key: columnKey,
                label: `${param.label} - ${field.label}`,
                paramKey: param.key,
                fieldKey: field.key,
              });
            }
          });
        } else if (variant && variant.value_type === "multi_publisher") {
          if (!columnsMap.has(param.key)) {
            columnsMap.set(param.key, {
              key: param.key,
              label: param.label,
              paramKey: param.key,
              fieldKey: null,
            });
          }
        }
      });
    });

    return Array.from(columnsMap.values());
  };

  const getDueDateFields = (categoryId: string, adTypeId: string) => {
    if (!uiParametersByAdType || !categoryId || !adTypeId) {
      return [];
    }

    const adCategoryParams = uiParametersByAdType[categoryId];
    if (!adCategoryParams) return [];

    const adTypeData = adCategoryParams.find(
      (adType) => adType.id === adTypeId
    );
    if (!adTypeData) return [];

    const dueDatesParam = adTypeData.parameters.find(
      (param) => param.key === "due_dates"
    );

    if (!dueDatesParam) return [];

    const variant = dueDatesParam.variants.find(
      (v) => v.ad_type_id === adTypeId
    );

    if (!variant || !variant.fields) return [];

    return variant.fields.map((field) => ({
      key: `${dueDatesParam.key}_${field.key}`,
      label: field.label,
      type: field.type,
      paramKey: dueDatesParam.key,
      fieldKey: field.key,
      definitionId: dueDatesParam.definition_id,
    }));
  };

  const renderAssetTable = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ) => {
    const selectedCategoryData = adCategoryData.find(
      (cat) => cat.ad_category_id === categoryId
    );

    if (!selectedCategoryData) return null;

    const currentAssets = getCurrentAssets(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );
    const tableColumns = getTableColumns(categoryId, currentAssets);

    const getInputClasses = () => "w-full px-3 py-2 min-w-[150px]";

    const headerDueDateFields =
      currentAssets.length > 0 && currentAssets[0].adType
        ? getDueDateFields(categoryId, currentAssets[0].adType)
        : [];

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left relative">
          <thead className="bg-gray-100 text-gray-700 font-medium">
            <tr>
              <th className="py-3 px-4 min-w-[150px]">Publisher</th>
              <th className="py-3 px-4 min-w-[150px]">Ad Type</th>
              {tableColumns
                .filter((column) => !column.paramKey.includes("due_dates"))
                .map((column) => (
                  <th key={column.key} className="py-3 px-4 min-w-[150px]">
                    {column.label}
                  </th>
                ))}
              {headerDueDateFields.length > 0 ? (
                <>
                  {/* Header and body cells use the same fixed column widths so
                      each label sits directly above its own input. Previously
                      the header spread its labels with justify-between while the
                      body packed inputs left, so the two never lined up. */}
                  {/* `w-px` keeps this column at its content width. Without it
                      the table's min-w-full spread the leftover space into the
                      last column, leaving a wide empty grey area to the right
                      of the date inputs. */}
                  <th className="py-3 px-4 w-px rounded sticky right-0 bg-gray-200 z-20">
                    <div className="flex items-center gap-2 font-medium text-gray-600 whitespace-nowrap">
                      {headerDueDateFields.map((field) => (
                        <span key={field.key} className="w-[150px] shrink-0">
                          {field.label}
                        </span>
                      ))}
                      {/* Matches the delete button column in the body. */}
                      <span className="w-6 shrink-0" />
                    </div>
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {currentAssets.map((asset) => {
              const dueDateFields = getDueDateFields(categoryId, asset.adType);

              const renderPublisherField = () => {
                const publisher = selectedCategoryData.publishers?.[0];
                if (publisher?.type === "single") {
                  if (asset.publisher !== publisher.id) {
                    updateAssetField(
                      flightIndex,
                      languageId,
                      objectiveId,
                      regionName,
                      categoryId,
                      asset.id,
                      "publisher",
                      publisher.id
                    );
                  }

                  return (
                    <TextField
                      type="text"
                      value={publisher.name}
                      disabled
                      className={`${getInputClasses()} bg-gray-100`}
                    />
                  );
                } else if (publisher?.type === "multi") {
                  return (
                    <Dropdown
                      options={selectedCategoryData.publishers.map((pub) => ({
                        title: pub.name,
                        value: pub.id,
                      }))}
                      selected={asset.publisher}
                      setSelected={(value) =>
                        updateAssetField(
                          flightIndex,
                          languageId,
                          objectiveId,
                          regionName,
                          categoryId,
                          asset.id,
                          "publisher",
                          value
                        )
                      }
                    />
                  );
                } else {
                  return (
                    <TextField
                      type="text"
                      placeholder="Add publisher"
                      value={asset.publisher}
                      onChange={(e) =>
                        updateAssetField(
                          flightIndex,
                          languageId,
                          objectiveId,
                          regionName,
                          categoryId,
                          asset.id,
                          "publisher",
                          e.target.value
                        )
                      }
                      className={getInputClasses()}
                    />
                  );
                }
              };

              const renderAdTypeField = () => {
                let currentPublisherId = asset.publisher;

                if (
                  !currentPublisherId &&
                  selectedCategoryData.publishers?.[0]?.type === "single"
                ) {
                  currentPublisherId = selectedCategoryData.publishers[0]?.id;
                }

                // The following changes were made to handle user-defined publishers
                const filteredAdTypes =
                  selectedCategoryData.ad_types.filter(
                    (adType) => adType.publisher_id === currentPublisherId
                  ) || [];

                // ✅ fallback for user-defined publisher
                const isUserDefinedPublisher =
                  !selectedCategoryData.publishers.some(
                    (pub) => pub.id === currentPublisherId
                  );

                if (isUserDefinedPublisher) {
                  // show all ad types if user-defined publisher
                  filteredAdTypes.push(...selectedCategoryData.ad_types);
                }

                // End changes

                if (!currentPublisherId || !filteredAdTypes.length) {
                  return (
                    <Dropdown
                      options={[]}
                      selected=""
                      disabled
                      placeholder="Select Publisher"
                      setSelected={(e) => {}}
                    />
                  );
                }

                return (
                  <Dropdown
                    options={filteredAdTypes.map((adType) => ({
                      title: adType.name,
                      value: adType.id,
                    }))}
                    selected={asset.adType}
                    setSelected={(value) => {
                      updateAssetField(
                        flightIndex,
                        languageId,
                        objectiveId,
                        regionName,
                        categoryId,
                        asset.id,
                        "adType",
                        value
                      );
                      updateAssetField(
                        flightIndex,
                        languageId,
                        objectiveId,
                        regionName,
                        categoryId,
                        asset.id,
                        "fields",
                        {}
                      );
                    }}
                  />
                );
              };

              const renderDynamicFields = () => {
                if (!uiParametersByAdType || !asset.adType || !categoryId) {
                  return tableColumns
                    .filter((column) => !column.paramKey.includes("due_dates"))
                    .map((column) => (
                      <td key={column.key} className="py-3 px-4" />
                    ));
                }

                const adCategoryParams = uiParametersByAdType[categoryId];
                if (!adCategoryParams) {
                  return tableColumns
                    .filter((column) => !column.paramKey.includes("due_dates"))
                    .map((column) => (
                      <td key={column.key} className="py-3 px-4" />
                    ));
                }

                const adTypeData = adCategoryParams.find(
                  (adType) => adType.id === asset.adType
                );
                if (!adTypeData) {
                  return tableColumns
                    .filter((column) => !column.paramKey.includes("due_dates"))
                    .map((column) => (
                      <td key={column.key} className="py-3 px-4" />
                    ));
                }

                return tableColumns
                  .filter((column) => !column.paramKey.includes("due_dates"))
                  .map((column) => {
                    const param = adTypeData.parameters.find(
                      (p) => p.key === column.paramKey
                    );
                    if (!param) {
                      return <td key={column.key} className="py-3 px-4" />;
                    }

                    if (param.key === "due_dates") {
                      return <td key={column.key} className="py-3 px-4" />;
                    }

                    const variant = param.variants.find(
                      (v) => v.ad_type_id === asset.adType
                    );
                    if (!variant) {
                      return <td key={column.key} className="py-3 px-4" />;
                    }

                    if (variant.value_type === "user_defined") {
                      const field = variant.fields.find(
                        (f) => `${param.key}_${f.key}` === column.key
                      );

                      if (!field) {
                        return <td key={column.key} className="py-3 px-4" />;
                      }

                      const fieldKey = column.key;

                      if (
                        field.type === "select" ||
                        field.type === "dropdown"
                      ) {
                        const foundField = parameterDefinitions[
                          variant.media_type
                        ]
                          ?.flatMap((item) => item.fields)
                          ?.find((e) => e.id === field.parameter_field_id);
                        const fieldOptions = Array.isArray(foundField?.options)
                          ? foundField.options
                          : [];

                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <Dropdown
                              options={fieldOptions.map((opt: any) => ({
                                title:
                                  opt.title ||
                                  opt.label ||
                                  opt.name ||
                                  String(opt),
                                value:
                                  opt.value ||
                                  opt.id ||
                                  opt.title ||
                                  String(opt),
                              }))}
                              selected={asset.fields?.[fieldKey] || ""}
                              setSelected={(value) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: value,
                                  }
                                );
                              }}
                            />
                          </td>
                        );
                      } else if (field.type === "number") {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="number"
                              placeholder={field.label}
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      } else if (field.type === "date") {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <DatePickerField
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(date) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: date,
                                  }
                                );
                              }}
                              compact
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      } else if (field.type === "list|text") {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="text"
                              placeholder={field.label}
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      } else {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="text"
                              placeholder={field.label}
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      }
                    } else if (variant.value_type === "single_publisher") {
                      return <td key={column.key} className="py-3 px-4" />;
                    } else if (variant.value_type === "multi_publisher") {
                      let options: Array<{ title: string; value: string }> = [];
                      const fields = variant.fields;

                      if (fields && fields.length > 0) {
                        const firstFieldWithArray = fields.find((field) =>
                          Array.isArray(field.value)
                        );

                        if (
                          firstFieldWithArray &&
                          Array.isArray(firstFieldWithArray.value)
                        ) {
                          const arrayLength = firstFieldWithArray.value.length;

                          options = Array.from({ length: arrayLength }).map(
                            (_, i) => {
                              const entryParts: string[] = [];
                              const valueParts: string[] = [];

                              fields.forEach((field) => {
                                if (
                                  Array.isArray(field.value) &&
                                  field.value[i] !== undefined
                                ) {
                                  entryParts.push(
                                    `${field.label}: ${field.value[i]}`
                                  );
                                  valueParts.push(String(field.value[i]));
                                }
                              });

                              return {
                                title: entryParts.join(" - "),
                                value: valueParts.join("_"),
                              };
                            }
                          );
                        }
                      }

                      return (
                        <td key={column.key} className="py-3 px-4">
                          <Dropdown
                            options={options}
                            selected={asset.fields?.[column.paramKey] || ""}
                            setSelected={(value) => {
                              updateAssetFields(
                                flightIndex,
                                languageId,
                                objectiveId,
                                regionName,
                                categoryId,
                                asset.id,
                                {
                                  [column.paramKey]: value,
                                }
                              );
                            }}
                          />
                        </td>
                      );
                    }

                    return <td key={column.key} className="py-3 px-4" />;
                  });
              };

              return (
                <tr
                  key={asset.id}
                  className={`border-t border-gray-100 ${asset.locked ? "bg-gray-50" : ""}`}
                  title={
                    asset.locked
                      ? "Briefed: this asset is locked and keeps the values it was briefed with."
                      : undefined
                  }
                >
                  <td className="py-3 px-4" inert={asset.locked || undefined}>
                    <div className={asset.locked ? "opacity-60" : undefined}>
                      {renderPublisherField()}
                    </div>
                    {asset.locked && (
                      <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                        <Lock size={12} /> Briefed
                      </span>
                    )}
                  </td>
                  <td
                    className={`py-3 px-4 ${asset.locked ? "opacity-60" : ""}`}
                    inert={asset.locked || undefined}
                  >
                    {renderAdTypeField()}
                  </td>
                  {asset.locked
                    ? React.Children.map(renderDynamicFields(), (cell) =>
                        React.isValidElement<{ className?: string; inert?: boolean }>(cell)
                          ? React.cloneElement(cell, {
                              inert: true,
                              className: `${cell.props.className ?? ""} opacity-60`,
                            })
                          : cell
                      )
                    : renderDynamicFields()}

                  {dueDateFields.length > 0 ? (
                    <>
                      <td className="py-3 px-4 w-px sticky right-0 bg-gray-200 z-10 rounded">
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex items-center gap-2 ${asset.locked ? "opacity-60" : ""}`}
                            inert={asset.locked || undefined}
                          >
                          {dueDateFields.map((dueDateField) =>
                            dueDateField.type === "date" ? (
                              <DatePickerField
                                key={dueDateField.key}
                                placeholder={dueDateField.label}
                                value={asset.fields?.[dueDateField.key] || ""}
                                onChange={(date) => {
                                  updateAssetFields(
                                    flightIndex,
                                    languageId,
                                    objectiveId,
                                    regionName,
                                    categoryId,
                                    asset.id,
                                    {
                                      [dueDateField.key]: date,
                                    }
                                  );
                                }}
                                compact
                                className="w-[150px] shrink-0"
                              />
                            ) : (
                              <TextField
                                key={dueDateField.key}
                                type="text"
                                placeholder={dueDateField.label}
                                value={asset.fields?.[dueDateField.key] || ""}
                                onChange={(e) => {
                                  updateAssetFields(
                                    flightIndex,
                                    languageId,
                                    objectiveId,
                                    regionName,
                                    categoryId,
                                    asset.id,
                                    {
                                      [dueDateField.key]: e.target.value,
                                    }
                                  );
                                }}
                                className="text-xs px-2 py-1 w-[150px] shrink-0"
                              />
                            )
                          )}
                          </div>
                          {/* A saved asset is deleted server-side, so it gets a
                              confirmation first. An unsaved row only exists in
                              this form, so it is removed straight away. */}
                          {isPersistedAsset(asset) ? (
                            <DeleteConfirmationControl
                              itemLabel="asset"
                              ariaLabel="Delete asset"
                              title="Delete asset?"
                              description="This removes the asset, its versions, and its stored media from the campaign. The records and media are retained for 30 days, and an export of everything deleted is emailed to you."
                              confirmLabel="Delete asset"
                              iconSize={14}
                              triggerClassName="w-6 h-6 shrink-0 !text-red-500 hover:!text-red-700 hover:bg-red-100 !rounded-full flex items-center justify-center"
                              onConfirm={() =>
                                deleteAsset(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id
                                )
                              }
                              onError={(message) => setError(message)}
                            />
                          ) : (
                            <button
                              onClick={() =>
                                deleteAsset(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id
                                )
                              }
                              className="w-6 h-6 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full flex items-center justify-center"
                              title="Delete row"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Monil's Code for converting to output JSON
  const convertToOutputJSON = () => {
    const outputData: any[] = [];
    const flightMap = new Map<string, any>();

    assetGroups.forEach((group) => {
      if (!flightMap.has(group.flight_id)) {
        flightMap.set(group.flight_id, {
          flight_id: group.flight_id,
          flight_rows: [],
        });
      }

      const flightOutput = flightMap.get(group.flight_id);

      // Find existing flight_row or create new one
      let flightRow = flightOutput.flight_rows.find(
        (row: any) => row.flight_row_id === group.flight_row_id
      );

      if (!flightRow) {
        flightRow = {
          flight_row_id: group.flight_row_id,
          flight_ad_categories: [],
        };
        flightOutput.flight_rows.push(flightRow);
      }

      const flightAdCategory: any = {
        flight_ad_categories_id: group.flight_ad_categories_id,
        ad_rows: [],
      };

      // Get version count from adCategoryData
      const categoryData = adCategoryData.find(
        (cat) => cat.ad_category_id === group.category_id
      );

      // Convert each asset to ad_row
      group.assets.forEach((asset) => {
        // Skip incomplete assets
        if (!asset.adType || !asset.publisher) return;

        // A saved Box is one the server gave us a primary key for. This used to be a
        // lookup into the old ad_type/publisher nesting, which reported a Box as new
        // whenever a sibling had overwritten it in that map.
        const isExistingAsset = isPersistedAsset(asset);

        const adRow: any = {
          ad_row_id: asset.id,
          parameters: [],
        };

        // The ad type and category are always sent, for saved rows too.
        // `AssetsService::updateAssets()` falls back to the row's stored value when
        // `ad_type_id` is absent, so omitting it on a saved row silently discards a
        // change the user made to the ad type.
        adRow.ad_type_id = asset.adType;
        adRow.ad_category_id = group.category_id;

        // `versions` stays new-row-only: re-asserting the category default on a saved
        // row can trim asset versions that already exist.
        if (!isExistingAsset) {
          adRow.versions = categoryData?.versions || 1;
        }

        // Always send publisher fields for all assets
        const knownPublisherIds =
          categoryData?.publishers.map((p) => p.id) || [];
        const isUserDefinedPublisher = !knownPublisherIds.includes(
          asset.publisher
        );

        if (isUserDefinedPublisher) {
          adRow.is_publisher_user_defined = true;
          adRow.publisher_name = asset.publisher;
          adRow.publisher_id = null;
        } else {
          adRow.is_publisher_user_defined = false;
          adRow.publisher_id = asset.publisher;
          adRow.publisher_name =
            categoryData?.publishers.find((p) => p.id === asset.publisher)
              ?.name || "";
        }

        const adCategoryParams = uiParametersByAdType?.[group.category_id];
        const adTypeParams = adCategoryParams?.find(
          (adType) => adType.id === asset.adType
        );

        if (asset.locked && asset.savedParameters) {
          // A briefed Box is locked: send back exactly what it already holds, so
          // a newer ad-category version cannot make it read as edited (which the
          // server rejects for the whole save with 423 ASSET_LOCKED).
          adRow.parameters = asset.savedParameters.map((param) => ({ ...param }));
        } else if (adTypeParams) {
          // Process all parameters including single_publisher
          adTypeParams.parameters.forEach((param) => {
            const variant = param.variants.find(
              (v) => v.ad_type_id === asset.adType
            );

            if (!variant) return;

            if (variant.value_type === "single_publisher") {
              // Handle single_publisher: Add all fields from variant
              if (variant.fields && variant.fields.length > 0) {
                variant.fields.forEach((field) => {
                  if (field.value !== null && field.value !== undefined) {
                    adRow.parameters.push({
                      parameter_definition_id: param.definition_id,
                      parameter_key: field.key,
                      parameter_value: field.value.toString(),
                    });
                  }
                });
              }
            } else if (variant.value_type === "user_defined") {
              // Handle user_defined: Get values from asset.fields
              if (variant.fields && variant.fields.length > 0) {
                variant.fields.forEach((field) => {
                  const fieldKey = `${param.key}_${field.key}`;
                  const fieldValue = asset.fields?.[fieldKey];

                  if (
                    fieldValue !== null &&
                    fieldValue !== undefined &&
                    fieldValue !== ""
                  ) {
                    adRow.parameters.push({
                      parameter_definition_id: param.definition_id,
                      parameter_key: field.key,
                      parameter_value: fieldValue,
                    });
                  }
                });
              }
            } else if (variant.value_type === "multi_publisher") {
              // Handle multi_publisher: Get value from asset.fields
              const fieldValue = asset.fields?.[param.key];

              if (
                fieldValue !== null &&
                fieldValue !== undefined &&
                fieldValue !== ""
              ) {
                adRow.parameters.push({
                  parameter_definition_id: param.definition_id,
                  parameter_key: param.key,
                  parameter_value: fieldValue,
                });
              }
            }
          });
        }

        if (asset.publisher && asset.adType) {
          flightAdCategory.ad_rows.push(adRow);
        }
      });

      if (flightAdCategory.ad_rows.length > 0) {
        flightRow.flight_ad_categories.push(flightAdCategory);
      }
    });

    // Convert map to array
    flightMap.forEach((flight) => {
      if (flight.flight_rows.length > 0) {
        outputData.push(flight);
      }
    });

    return {
      campaign_id: searchParams.get("id") || "",
      data: outputData,
    };
  };

  const convertExistingAssetsToLocalState = (
    existingAssets: any,
    flightId: string,
    flightIndex: number,
    languageId: string,
    languageName: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    flightRowId: string,
    flightAdCategoriesId: string
  ): Asset[] => {
    if (!existingAssets || !existingAssets[categoryId]) {
      return [];
    }

    const categoryData = existingAssets[categoryId];
    const assets: Asset[] = [];

    // A flat list of Boxes, so two Boxes sharing an ad type and publisher both survive.
    (categoryData.assets ?? []).forEach((entry: any) => {
      const assetMeta = entry?.asset_meta;
      const adTypeId = assetMeta?.ad_type_id;
      const publisherId = assetMeta?.publisher_id;

      if (assetMeta && adTypeId && publisherId) {
        // Match by flight_row_id and ad_category_id (more stable than flight_ad_categories_id)
        if (
          assetMeta.flight_row_id === flightRowId &&
          assetMeta.ad_category_id === categoryId
        ) {
          const fields: { [key: string]: any } = {};

          // Process parameters from parameter_value_pair
          if (
            assetMeta.parameter_value_pair &&
            assetMeta.parameter_value_pair.parameters
          ) {
            assetMeta.parameter_value_pair.parameters.forEach((param: any) => {
              const adCategoryParams = uiParametersByAdType?.[categoryId];
              if (adCategoryParams) {
                const adTypeParams = adCategoryParams.find(
                  (at) => at.id === adTypeId
                );
                if (adTypeParams) {
                  const paramDef = adTypeParams.parameters.find(
                    (p) => p.definition_id === param.definition_id
                  );

                  if (paramDef) {
                    const variant = paramDef.variants.find(
                      (v) => v.ad_type_id === adTypeId
                    );

                    if (variant && variant.value_type === "user_defined") {
                      const fieldKey = `${paramDef.key}_${param.key}`;
                      fields[fieldKey] = param.value;
                    } else if (
                      variant &&
                      variant.value_type === "multi_publisher"
                    ) {
                      fields[paramDef.key] = param.value;
                    }
                  } else {
                    fields[param.key] = param.value;
                  }
                }
              }
            });
          }

          assets.push({
            // `id` stays the match key the save path sends as `ad_row_id`; `assetId` is
            // the row's real identity, used for deletes and the saved/new distinction.
            id: assetMeta.assets_row_id,
            assetId: assetMeta.asset_id,
            publisher: publisherId,
            adType: adTypeId,
            fields: fields,
            locked: Boolean(assetMeta.is_locked),
            savedParameters: (
              assetMeta.parameter_value_pair?.parameters ?? []
            ).map((param: any) => ({
              parameter_definition_id: param.definition_id,
              parameter_key: param.key,
              parameter_value: param.value,
            })),
          });
        }
      }
    });

    return assets;
  };

  const saveData = async () => {
    try {
      const outputJSON = convertToOutputJSON();

      if (assetsValues) {
        await updateAssets(outputJSON);
        showSuccess("Assets Updated Successfully");
      } else {
        await createAssets(outputJSON);
        showSuccess("Assets created Successfully");
      }

      navigate(`/campaign/summary?id=${searchParams.get("id")}`);
    } catch (err: unknown) {
      if (err instanceof z.ZodError) {
        err.issues.forEach((issue) => {
          console.warn(
            `Field: ${issue.path.join(".")}, Error: ${issue.message}`
          );
        });

        const firstIssue = err.issues[0];
        setError(firstIssue?.message || "Validation failed");
      } else {
        setError((err as Error).message || "Something went wrong");
      }
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const campaignId = searchParams.get("id");
      if (campaignId) {
        await getAssetsPageData(campaignId);
        await getAdCategoryData(campaignId);
      }
    } catch (error: any) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (assetsValues && assetsPageData && assetsPageData.length > 0) {
      const newAssetGroups: AssetGroup[] = [];

      assetsPageData.forEach((flight, flightIndex) => {
        flight.languages.forEach((language) => {
          language.objectives.forEach((objective) => {
            objective.locations.forEach((location) => {
              const flightRowsObj = location.flight_rows;
              if (!flightRowsObj) return;

              const flightRowIds = Object.keys(flightRowsObj);
              if (flightRowIds.length === 0) return;

              const firstFlightRowId = flightRowIds[0];
              const flightRowData = flightRowsObj[firstFlightRowId];
              const adCategories = flightRowData?.ad_categories || [];

              adCategories.forEach((adCategory: any) => {
                const existingAssets = convertExistingAssetsToLocalState(
                  assetsValues,
                  flight.flight_id,
                  flightIndex,
                  language.language_id,
                  language.language_name,
                  objective.objective_id,
                  location.region,
                  adCategory.ad_category_id,
                  firstFlightRowId,
                  adCategory.flight_ad_categories_id
                );

                if (existingAssets.length > 0) {
                  newAssetGroups.push({
                    flight_id: flight.flight_id,
                    flight_index: flightIndex,
                    language_id: language.language_id,
                    language_name: language.language_name,
                    objective_id: objective.objective_id,
                    region_name: location.region,
                    category_id: adCategory.ad_category_id,
                    flight_row_id: firstFlightRowId,
                    flight_ad_categories_id: adCategory.flight_ad_categories_id,
                    assets: existingAssets,
                  });
                }
              });
            });
          });
        });
      });

      if (newAssetGroups.length > 0) {
        setAssetGroups(newAssetGroups);
      }
    }
  }, [assetsValues, assetsPageData, uiParametersByAdType]);

  useEffect(() => {
    if (
      assetsPageData &&
      assetsPageData.length > 0 &&
      assetsPageData[selectedFlight]?.languages.length > 0 &&
      selectedLanguage === ""
    ) {
      setSelectedLanguage("all");
    }
  }, [assetsPageData, selectedFlight]);

  if (!campaign) {
    return <Navigate to="/dashboard" />;
  }

  const languagesToDisplay =
    selectedLanguage === "all"
      ? assetsPageData?.[selectedFlight]?.languages || []
      : assetsPageData?.[selectedFlight]?.languages.filter(
          (lang) => lang.language_id === selectedLanguage
        ) || [];

  return (
    <CampaignDetailWrapper>
      <div className="mb-8">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1 min-w-0 overflow-x-auto pr-5 scrollbar-hidden">
            <div className="flex w-max space-x-0">
              {assetsPageData &&
                assetsPageData.length > 0 &&
                assetsPageData.map((flight, index) => (
                  <button
                    key={flight.flight_number}
                    onClick={() => setSelectedFlight(index)}
                    className={`px-10 py-2 font-medium transition-colors border ${
                      selectedFlight === index
                        ? "bg-[#EA33C0] text-white border-[#EA33C0]"
                        : "bg-white text-gray-700 border-[#EA33C0] hover:bg-pink-50"
                    } ${
                      index === 0
                        ? "rounded-l-full"
                        : index === assetsPageData.length - 1
                        ? "rounded-r-full border-l-0"
                        : "border-l-0"
                    }`}
                  >
                    Flight {index + 1}
                  </button>
                ))}
            </div>
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <span className="text-gray-700">Language</span>
            <Dropdown
              options={[
                { title: "All Languages", value: "all" },
                ...(assetsPageData?.[selectedFlight]?.languages.map((lang) => ({
                  title: lang.language_name,
                  value: lang.language_id,
                })) || []),
              ]}
              selected={selectedLanguage}
              setSelected={setSelectedLanguage}
              className="min-w-36"
            />
          </div>

          <Button
            type="secondary"
            leadingIcon={<Settings className="w-4 h-4" />}
            className="w-auto shrink-0"
            onClick={() => {
              navigate(`/campaign/flight?id=${searchParams.get("id")}`);
            }}
          >
            Edit Flights
          </Button>
        </div>
      </div>

      {isLoading && <div className="text-center py-8">Loading...</div>}

      {!isLoading && (!assetsPageData || assetsPageData.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No assets data available. Please create flights first.
        </div>
      )}

      {!isLoading &&
        languagesToDisplay.length > 0 &&
        languagesToDisplay.map((currentLanguageData) => (
          <div key={currentLanguageData.language_id}>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {currentLanguageData.language_name} Assets
            </h3>

            <hr className="border-gray-300 mb-5" />

            {currentLanguageData.objectives.map((objective) =>
              objective.locations.map((location) => {
                const regionKey = `${objective.objective_id}-${location.region}`;
                const flightData = assetsPageData[selectedFlight];

                // Get flight_rows object
                const flightRowsObj = location.flight_rows;
                if (!flightRowsObj) return null;

                // Get first flight_row_id
                const flightRowIds = Object.keys(flightRowsObj);
                if (flightRowIds.length === 0) return null;

                const firstFlightRowId = flightRowIds[0];
                const flightRowData = flightRowsObj[firstFlightRowId];

                // Get ad_categories from the flight_row
                const adCategories = flightRowData?.ad_categories || [];

                // Get first category as default if not selected
                const selectedCategoryId =
                  getSelectedCategory(regionKey) ||
                  adCategories[0]?.ad_category_id ||
                  "";

                // Find the flight_ad_categories_id for the selected category
                const selectedCategoryInfo = adCategories.find(
                  (cat: any) => cat.ad_category_id === selectedCategoryId
                );

                // Initialize or get asset group
                if (selectedCategoryId && selectedCategoryInfo) {
                  getOrCreateAssetGroup(
                    flightData.flight_id,
                    selectedFlight,
                    currentLanguageData.language_id,
                    currentLanguageData.language_name,
                    objective.objective_id,
                    location.region,
                    selectedCategoryId,
                    firstFlightRowId,
                    selectedCategoryInfo.flight_ad_categories_id
                  );

                  // Set selected category if not set
                  if (!getSelectedCategory(regionKey)) {
                    setSelectedCategory(regionKey, selectedCategoryId);
                  }
                }

                return (
                  <div key={regionKey} className="mb-8">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {objective.objective_name} / {location.region}
                        </h4>
                        <p className="text-gray-600">
                          {adCategories.length} Assets
                        </p>
                      </div>

                      <div className="flex space-x-2">
                        {adCategories.map((cat: any) => {
                          const assetCount = getAssetCount(
                            selectedFlight,
                            currentLanguageData.language_id,
                            objective.objective_id,
                            location.region,
                            cat.ad_category_id
                          );

                          return (
                            <button
                              key={cat.ad_category_id}
                              onClick={() => {
                                setSelectedCategory(
                                  regionKey,
                                  cat.ad_category_id
                                );
                                getOrCreateAssetGroup(
                                  flightData.flight_id,
                                  selectedFlight,
                                  currentLanguageData.language_id,
                                  currentLanguageData.language_name,
                                  objective.objective_id,
                                  location.region,
                                  cat.ad_category_id,
                                  firstFlightRowId,
                                  cat.flight_ad_categories_id
                                );
                              }}
                              className={`border inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                getSelectedCategory(regionKey) ===
                                cat.ad_category_id
                                  ? "bg-gray-700 text-white"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {cat.ad_category_name}
                              <span className="ml-4 text-xs">{assetCount}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <hr className="border-gray-300 mb-4" />

                    {selectedCategoryId && (
                      <>
                        <div className="flex items-center justify-end gap-3 mb-3">
                          <span className={`text-sm font-medium ${
                            isApplyToAllActive(
                              selectedFlight,
                              currentLanguageData.language_id,
                              objective.objective_id,
                              location.region,
                              selectedCategoryId
                            )
                              ? "text-[#EA33C0]"
                              : "text-gray-500"
                          }`}>
                            Apply to same publisher
                          </span>
                          <ToggleSwitch
                            enabled={isApplyToAllActive(
                              selectedFlight,
                              currentLanguageData.language_id,
                              objective.objective_id,
                              location.region,
                              selectedCategoryId
                            )}
                            setEnabled={() =>
                              toggleApplyToAll(
                                selectedFlight,
                                currentLanguageData.language_id,
                                objective.objective_id,
                                location.region,
                                selectedCategoryId
                              )
                            }
                          />
                        </div>

                        {renderAssetTable(
                          selectedFlight,
                          currentLanguageData.language_id,
                          objective.objective_id,
                          location.region,
                          selectedCategoryId
                        )}

                        <Button
                          type="secondary"
                          leadingIcon={<Plus className="w-4 h-4" />}
                          onClick={() =>
                            addAsset(
                              selectedFlight,
                              currentLanguageData.language_id,
                              objective.objective_id,
                              location.region,
                              selectedCategoryId
                            )
                          }
                          className="mt-4"
                        >
                          Add Row
                        </Button>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ))}

      <Button type="primary" onClick={saveData}>
        Save Changes
      </Button>
    </CampaignDetailWrapper>
  );
};

export default AssetSetup;
